import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { theme } from 'assets/styles/theme';
import { CreateButton } from 'components/buttons/CreateButton';
import { LoadingContainer } from 'components/containers/Containers';
import { LoadingIndicator } from 'components/ui-elements/LoadingIndicator';
import { useItemPopup } from 'features/add-item/form/useItemPopup';
import { getNoContentConfig } from 'features/my-profile/my-activity/utils/noContentConfig';
import { useMyActivityNavigate } from 'features/my-profile/my-activity/utils/useMyActivityNavigate';
import { useMyItemNavigate } from 'features/my-profile/my-activity/utils/useMyItemNavigate';
import { useResponsive } from 'hooks/useResponsive';
import { ROUTE_ITEMS } from 'navigation/config/routePaths';
import useMyItems, { ItemFilter } from 'services/useMyItems';

import { FilterButtons } from './FilterButtons';
import { MyActivityItemTile } from './MyActivityItemTile';
import { MyActivitySectionTitle } from './MyActivitySectionTitle';
import { MyActivitySeeAllButton } from './MyActivitySeeAllButton';
import { NoContent } from './NoContent';

const Container = styled.div`
  margin-top: 48px;

  ${theme.mq.phone} {
    margin-top: 24px;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  ${theme.mq.phone} {
    gap: 8px;
  }
`;

const InnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;

  ${theme.mq.phone} {
    gap: 8px;
  }
`;

const RightControls = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex-shrink: 0;

  ${theme.mq.phone} {
    gap: 8px;
  }
`;

const TileContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  ${theme.mq.tablet} {
    gap: 12px;
  }

  ${theme.mq.phone} {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ITEMS_PREVIEW_SIZE_DESKTOP = 3;
const ITEMS_PREVIEW_SIZE_MOBILE = 2;

export const HotDealsSection = () => {
  const { t } = useTranslation('myProfile');
  const [filter, setFilter] = useState(ItemFilter.SELLING);
  const { isMobile } = useResponsive();
  const previewSize = isMobile ? ITEMS_PREVIEW_SIZE_MOBILE : ITEMS_PREVIEW_SIZE_DESKTOP;
  const { state } = useMyItems({ filter, pageSize: previewSize });
  const showSeeAll = !state.loading && state.hasNextPage;
  const navigate = useNavigate();
  const { showAddItemPopup } = useItemPopup();
  const onAddClick = () => showAddItemPopup();
  const onBuyClick = () => navigate(ROUTE_ITEMS);
  const { goToHotDeals } = useMyActivityNavigate();
  const { goToMyItem } = useMyItemNavigate();

  const renderLoading = () => (
    <LoadingContainer>
      <LoadingIndicator />
    </LoadingContainer>
  );

  const renderItems = () => (
    <TileContainer>
      {state.items.map(item => (
        <MyActivityItemTile
          key={`${item.id}_${item.orderId}`}
          item={item}
          onClick={(itemId, orderId) => goToMyItem(filter, itemId, orderId)}
        />
      ))}
    </TileContainer>
  );

  const renderNoContent = () => {
    const noContentConfig = getNoContentConfig(onAddClick, onBuyClick);
    const { text, buttonText, onClick } = noContentConfig[filter];
    return <NoContent text={text} buttonText={buttonText} onClick={onClick} />;
  };

  const resolveButtonText = () => (isMobile ? t('my-activity.add') : t('my-activity.add-item'));

  const renderContent = () => {
    if (state.loading) return renderLoading();
    if (state.items && state.items.length > 0) return renderItems();
    return renderNoContent();
  };

  return (
    <Container data-testid={'hot-deals-section'}>
      <HeaderContainer>
        <InnerContainer>
          <MyActivitySectionTitle>{t('my-activity.hot-deals')}</MyActivitySectionTitle>
          <RightControls>
            <CreateButton onClick={onAddClick}>{resolveButtonText()}</CreateButton>
            {showSeeAll && (
              <MyActivitySeeAllButton onClick={() => goToHotDeals(filter)}>
                {t('my-activity.see-all')}
              </MyActivitySeeAllButton>
            )}
          </RightControls>
        </InnerContainer>
        <FilterButtons selectedFilter={filter} handleFilterChange={setFilter} />
      </HeaderContainer>
      {renderContent()}
    </Container>
  );
};
